import { Box, Chip, Paper } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import {
  FormMode,
  FormSection,
} from '~/components/molecules/FormSectionLayout/FormSectionLayout.tsx';
import {
  AddressFinderOption,
  Alert,
  FormSectionLayout,
  HookFormSwitch,
  CustomerFormSourceMapping,
  StyledLink,
  StyleOverrides,
} from '~/index.ts';
import ContactSection from './Sections/ContactSection.tsx';
import {
  CustomerSaveContext,
  CustomerSchema,
} from './CustomerFormProvider.tsx';
import { customerFormEventsName } from './constant.ts';
import LocationSection from './Sections/LocationSection.tsx';
import CoapplicantLocationSection from './Sections/CoapplicantLocationSection.tsx';
import CoapplicantSection from './Sections/CoapplicantSection.tsx';

import { isEmpty, merge } from 'lodash-es';
import { CustomerFormTexts, defaultCustomerFormTexts } from './formTexts.ts';
import { CrmType } from '@upstart/harmony/lib/constants.js';
import { DeepPartial } from '~/utils/types.ts';
import {
  CustomerFormDeal,
  CustomerInfoConfig,
  customerSchemaToUpdateApplicant,
  dealToCustomerSchema,
  getCreditRange,
  getDealMapping,
  getRequiredFields,
} from './utils/formHelper.ts';
import UnsavedDialog from './UnsavedDialog.tsx';
import LeadSection from './Sections/LeadSection.tsx';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes.js';
import { useCustomerForm } from '~/components/organisms/CustomerForm/useCustomerForm.ts';
import ResetCreditAppConfirmation from './ResetCreditAppDialog.tsx';
import { CustomerFormValidations } from './types.ts';
import { useApplyExternalValidations } from './hooks.ts';

export enum CustomerFormSections {
  isInShowroom = 'isInShowroom',
  contact = 'contact',
  lead = 'lead',
  address = 'address',
  addCoapplicantToggle = 'addCoapplicantToggle',
  coapplicant = 'coapplicant',
  coapplicantAddress = 'coapplicantAddress',
}

type CustomerFormEventType =
  (typeof customerFormEventsName)[keyof typeof customerFormEventsName];

export interface CustomerSectionProps {
  formTexts: CustomerFormTexts;
  control: Control<CustomerSchema>;
  formMode: FormMode;
  isEditing?: boolean;
  isOptional?: boolean;
  layoutOverrides?: CustomerFormLayoutOverrides;
  /** @description When true all inputs in the section are disabled */
  disabled?: boolean;
}

export interface CustomerFormLayoutOverrides {
  disableAddressAddEdit?: boolean;
  hideCoApplicant?: boolean;
  inShowroomDisplayLocation?: 'top' | 'bottom';
  select?: {
    size?: 'small' | 'medium';
  };
  textField?: {
    size?: 'small' | 'medium';
  };
  paper?: {
    elevation?: number;
    backgroundColor?: string;
  };
  inShowRoomStyleOverrides?: StyleOverrides;
  lockLeads?: boolean;
  customerForm?: {
    elevation?: number;
    backgroundColor?: string;
    hideValidationAlert?: boolean;
  };
}

export interface CustomerFormProps {
  deal: Deal;
  textOverrides?: DeepPartial<CustomerFormTexts>;
  layoutOverrides?: CustomerFormLayoutOverrides;
  onTrack?: (event: CustomerFormEventType) => void;
  onZipChange?: (zip: string) => void;
  onCoapplicantZipChange?: (zip: string) => void;
  dealerSourceMapping: CustomerFormSourceMapping;
  counties?: Array<string>;
  coapplicantCounties?: Array<string>;
  addressFinderOptions: AddressFinderOption[];
  coapplicantAddressFinderOptions?: AddressFinderOption[];
  dealerCrmType: CrmType;
  onAddressChange: (address: string) => void;
  onCoapplicantAddressChange?: (address: string) => void;
  onSelectedAddress?: (option: AddressFinderOption) => void;
  onEditSave?: (updateDeal: CustomerFormDeal) => Promise<void>;
  onResetCreditApp?: () => void;
  creditAppUrl?: string;
  customerInfoConfig?: CustomerInfoConfig;
  validations?: CustomerFormValidations;
  /** @description When true all controls that can change form state are disabled */
  disabled?: boolean;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  deal,
  textOverrides,
  layoutOverrides,
  onZipChange,
  onCoapplicantZipChange,
  dealerCrmType,
  counties,
  coapplicantCounties,
  addressFinderOptions,
  coapplicantAddressFinderOptions,
  dealerSourceMapping,
  onAddressChange,
  onEditSave,
  onSelectedAddress,
  onCoapplicantAddressChange,
  creditAppUrl,
  onResetCreditApp,
  customerInfoConfig,
  validations,
  disabled,
}) => {
  const formTexts = merge({}, defaultCustomerFormTexts, textOverrides);
  const {
    control,
    formState,
    watch,
    setValue,
    reset,
    resetField,
    trigger,
    getValues,
  } = useFormContext<CustomerSchema>();
  const [
    currentContact,
    currentLead,
    currentAddress,
    currentCoapplicant,
    currentCoapplicantAddress,
    currentRequiredFieldConfig,
    currentAddCoapplicantToggle,
  ] = watch([
    'contact',
    'lead',
    'address',
    'coapplicant',
    'coapplicantAddress',
    'requiredFieldConfig',
    'addCoapplicantToggle',
  ]);
  const [editKey, setEditKey] = useState<keyof CustomerSchema | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { isSubmitted, isValid } = formState;
  const { track, submit, formMode, setFormMode } = useCustomerForm();
  const [
    shouldCoApplicantAddressEditRemainOpen,
    setShouldCoApplicantAddressEditRemainOpen,
  ] = useState(false);

  const [shouldAddressEditRemainOpen, setShouldAddressEditRemainOpen] =
    useState(!!layoutOverrides?.disableAddressAddEdit);

  const { showUnsavedDialog, onClose, setShowUnsavedDialog } =
    useContext(CustomerSaveContext);

  const [showResetCreditDialog, setShowResetCreditDialog] = useState(false);

  useEffect(() => {
    if (currentAddress?.zip && onZipChange) {
      onZipChange(currentAddress?.zip);
    }
    if (currentCoapplicantAddress?.zip && onCoapplicantZipChange) {
      onCoapplicantZipChange(currentCoapplicantAddress?.zip);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAddress?.zip, currentCoapplicantAddress?.zip]);

  useEffect(() => {
    /*
    Setting the names when the Id's change.
    This is a retrofit because the current implementation
    in Instore/Papyrus are inconsistent on whether they save
    Name or ID of each map. This form will always save both.
  */
    const { sourceMap, secondaryMap, trackingMap } = getDealMapping(
      dealerSourceMapping ?? {},
      currentLead,
    );

    if (sourceMap) {
      setValue('lead.sourceName', sourceMap.name, { shouldValidate: false });
    }
    if (secondaryMap) {
      setValue('lead.secondarySourceName', secondaryMap.name, {
        shouldValidate: false,
      });
    }
    if (trackingMap) {
      setValue('lead.trackingCodeName', trackingMap.name, {
        shouldValidate: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLead?.sourceId,
    currentLead?.secondaryId,
    currentLead?.trackingCode,
    dealerSourceMapping,
    setValue,
  ]);

  useEffect(() => {
    setFormMode(deal?.customerInfoSubmittedAt ? 'edit' : 'add');
    if (isEmpty(deal?.applicant)) {
      return;
    }

    const requiredFields = getRequiredFields(
      dealerSourceMapping,
      customerInfoConfig,
      dealerCrmType,
    );
    const loadedCustomer = dealToCustomerSchema(deal);
    loadedCustomer.requiredFieldConfig = requiredFields;
    reset(loadedCustomer, { keepDefaultValues: false });

    if (loadedCustomer.address.zip && onZipChange) {
      onZipChange(loadedCustomer.address.zip);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal]);

  useEffect(() => {
    const requiredFields = getRequiredFields(
      dealerSourceMapping,
      customerInfoConfig,
      dealerCrmType,
    );
    setValue('requiredFieldConfig', requiredFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfoConfig]);

  useApplyExternalValidations(validations);

  useEffect(() => {
    if (formMode === 'add') {
      track?.(customerFormEventsName.ADD_CUSTOMER_DETAILS_START);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode]);

  const onSelectAddressHandler = (
    selectedAddress: AddressFinderOption | null,
  ): void => {
    if (selectedAddress) {
      setValue('address.city', selectedAddress.data?.city || '');
      setValue('address.apt', selectedAddress.data?.apt || '');
      setValue('address.state', selectedAddress.data?.state || '');
      setValue('address.zip', selectedAddress.data?.zip || '');
      setValue('address.county', '');
      onSelectedAddress && onSelectedAddress(selectedAddress);
      return;
    }

    setValue('address.city', '');
    setValue('address.apt', '');
    setValue('address.state', '');
    setValue('address.zip', '');
    setValue('address.county', '');
  };

  const onSelectCoapplicantAddressHandler = (
    selectedAddress: AddressFinderOption | null,
  ): void => {
    if (selectedAddress) {
      setValue('coapplicantAddress.city', selectedAddress.data?.city || '');
      setValue('coapplicantAddress.apt', selectedAddress.data?.apt || '');
      setValue('coapplicantAddress.state', selectedAddress.data?.state || '');
      setValue('coapplicantAddress.zip', selectedAddress.data?.zip || '');
      setValue('coapplicantAddress.county', '');
      onSelectedAddress && onSelectedAddress(selectedAddress);
      return;
    }

    setValue('coapplicantAddress.city', '');
    setValue('coapplicantAddress.apt', '');
    setValue('coapplicantAddress.state', '');
    setValue('coapplicantAddress.zip', '');
    setValue('coapplicantAddress.county', '');
  };

  const onEditSaveHandler = async (
    sectionKey: CustomerFormSections,
  ): Promise<void> => {
    const save = async (currentCustomer: CustomerSchema): Promise<void> => {
      if (
        sectionKey !== CustomerFormSections.isInShowroom &&
        editKey === null
      ) {
        return;
      }

      if (onEditSave) {
        setIsSaving(true);
        const updatedDeal = customerSchemaToUpdateApplicant(
          currentCustomer,
          sectionKey,
        );
        updatedDeal && (await onEditSave(updatedDeal));
        setIsSaving(false);
      }
      if (sectionKey === CustomerFormSections.address) {
        setShouldAddressEditRemainOpen(false);
      }
      if (sectionKey === CustomerFormSections.coapplicantAddress) {
        setShouldCoApplicantAddressEditRemainOpen(false);
      }
      setEditKey(null);
    };

    const formIsValid = await trigger(sectionKey);
    if (formIsValid) {
      await save(getValues());

      if (sectionKey === CustomerFormSections.contact) {
        track?.(customerFormEventsName.EDIT_CONTACT_INFO_FINISH);
      }

      if (sectionKey === CustomerFormSections.lead) {
        track?.(customerFormEventsName.EDIT_LEAD_INFO_FINISH);
      }

      if (sectionKey === CustomerFormSections.address) {
        track?.(customerFormEventsName.EDIT_ADDRESS_FINISH);
      }
    }
  };

  const getSections = (editKey: string | null): FormSection[] => {
    const sections: FormSection[] = [];
    const onEditCancelContact = (): void => {
      setEditKey(null);
      reset();
      track?.(customerFormEventsName.EDIT_CONTACT_INFO_FINISH);
    };

    const onEditCancelCoapplicant = (): void => {
      setEditKey(null);
      reset();
    };

    sections.push({
      title: formTexts.contact?.title ?? '',
      key: CustomerFormSections.contact,
      onEditCancel: onEditCancelContact,
      content: formMode && (
        <ContactSection
          currentContact={currentContact}
          formMode={formMode}
          control={control}
          formTexts={formTexts}
          layoutOverrides={layoutOverrides}
          isEditing={editKey === CustomerFormSections.contact}
          disabled={disabled}
        />
      ),
    });
    if (dealerCrmType !== CrmType.STANDALONE) {
      const onEditCancelLead = (): void => {
        setEditKey(null);
        reset();
        track?.(customerFormEventsName.EDIT_LEAD_INFO_FINISH);
      };
      sections.push({
        title: formTexts.lead?.title ?? '',
        key: CustomerFormSections.lead,
        onEditCancel: onEditCancelLead,
        disableEdit: true,
        content: formMode && (
          <LeadSection
            currentLead={currentLead}
            formMode={formMode}
            dealerCrmType={dealerCrmType}
            formTexts={formTexts}
            layoutOverrides={layoutOverrides}
            control={control}
            sourceMapping={dealerSourceMapping}
            disabled={disabled}
          />
        ),
      });
    }
    const onEditCancelAddress = (): void => {
      setEditKey(null);
      setShouldAddressEditRemainOpen(false);
      if (formMode === 'edit') {
        reset();
        track?.(customerFormEventsName.EDIT_ADDRESS_FINISH);
        return;
      }

      resetField('address');
    };

    const onEditCancelCoapplicantAddress = (): void => {
      setEditKey(null);
      setShouldCoApplicantAddressEditRemainOpen(false);
      if (formMode === 'edit') {
        reset();
        return;
      }

      resetField('coapplicantAddress');
    };
    sections.push({
      title: formTexts.address?.title ?? '',
      key: CustomerFormSections.address,
      onEditCancel: onEditCancelAddress,
      allowAdd:
        !layoutOverrides?.disableAddressAddEdit &&
        !currentRequiredFieldConfig?.zip,
      shouldRemainInEditMode: shouldAddressEditRemainOpen,
      hasSectionValues: formMode === 'edit' && !!deal?.applicant?.address,
      content: formMode && (
        <LocationSection
          currentAddress={currentAddress}
          formMode={formMode}
          isEditing={
            editKey === CustomerFormSections.address ||
            shouldAddressEditRemainOpen
          }
          control={control}
          formTexts={formTexts}
          layoutOverrides={layoutOverrides}
          counties={counties}
          addressFinderOptions={addressFinderOptions}
          onAddressChange={onAddressChange}
          onSelectAddress={onSelectAddressHandler}
          isOptional={!currentRequiredFieldConfig?.zip}
          disabled={disabled}
        />
      ),
    });

    if (currentAddCoapplicantToggle) {
      sections.push({
        title: formTexts.coapplicant?.title ?? '',
        key: CustomerFormSections.coapplicant,
        onEditCancel: onEditCancelCoapplicant,
        content: formMode && (
          <CoapplicantSection
            currentContact={currentCoapplicant}
            formMode={formMode}
            control={control}
            formTexts={formTexts}
            layoutOverrides={layoutOverrides}
            isEditing={editKey === CustomerFormSections.coapplicant}
            disabled={disabled}
          />
        ),
      });

      sections.push({
        title: formTexts.coapplicantAddress?.title ?? '',
        key: CustomerFormSections.coapplicantAddress,
        onEditCancel: onEditCancelCoapplicantAddress,
        allowAdd: !currentRequiredFieldConfig?.zip,
        shouldRemainInEditMode: shouldCoApplicantAddressEditRemainOpen,
        hasSectionValues: formMode === 'edit' && !!deal?.coapplicant?.address,
        content: formMode && (
          <CoapplicantLocationSection
            currentAddress={currentCoapplicantAddress}
            formMode={formMode}
            isEditing={
              editKey === CustomerFormSections.coapplicantAddress ||
              shouldCoApplicantAddressEditRemainOpen
            }
            control={control}
            formTexts={formTexts}
            layoutOverrides={layoutOverrides}
            counties={coapplicantCounties}
            addressFinderOptions={coapplicantAddressFinderOptions}
            onAddressChange={onCoapplicantAddressChange}
            onSelectAddress={onSelectCoapplicantAddressHandler}
            isOptional={!currentRequiredFieldConfig?.zip}
            disabled={disabled}
          />
        ),
      });
    }
    return sections;
  };

  const inShowroomToggleComponent = (
    <HookFormSwitch
      control={control}
      name="isInShowroom"
      label={formTexts.customerInShowroomLabel}
      onChange={() => {
        if (formMode === 'edit') {
          onEditSaveHandler(CustomerFormSections.isInShowroom);
        }
      }}
      disabled={disabled || (!!editKey && formMode == 'edit')}
    />
  );

  const addCoapplicantToggleComponent = !layoutOverrides?.hideCoApplicant ? (
    <HookFormSwitch
      control={control}
      name="addCoapplicantToggle"
      label={formTexts.addCoapplicantToggleLabel}
      onChange={() => {
        if (formMode === 'edit') {
          onEditSaveHandler(CustomerFormSections.addCoapplicantToggle);
        }
      }}
      disabled={disabled || (!!editKey && formMode == 'edit')}
    />
  ) : null;

  const creditRange = getCreditRange(deal);
  const showCreditChip = creditAppUrl && creditRange && formMode === 'edit';
  const showCreditAppLink = creditAppUrl && formMode === 'edit';
  const inShowroomLocation =
    layoutOverrides?.inShowroomDisplayLocation ?? 'top';
  return (
    <Paper
      elevation={layoutOverrides?.customerForm?.elevation ?? 0}
      role="form"
      sx={{
        backgroundColor: layoutOverrides?.customerForm?.backgroundColor ?? '',
      }}
    >
      {!layoutOverrides?.customerForm?.hideValidationAlert &&
        isSubmitted &&
        !isValid && (
          <Alert severity="error" styleOverrides={{ margin: '0 0 24px' }}>
            Correct all errors below to continue
          </Alert>
        )}
      {showUnsavedDialog && (
        <UnsavedDialog
          onCancel={() => {
            setShowUnsavedDialog(false);
          }}
          onExit={() => {
            setShowUnsavedDialog(false);
            onClose();
            track?.(customerFormEventsName.ADD_CUSTOMER_DETAILS_FINISH);
          }}
          onSave={() => {
            setShowUnsavedDialog(false);
            submit();
          }}
        />
      )}
      {showResetCreditDialog && (
        <ResetCreditAppConfirmation
          onCancel={() => {
            setShowResetCreditDialog(false);
            setEditKey(null);
          }}
          onContinue={() => {
            if (onResetCreditApp) {
              onResetCreditApp();
            }

            setShowResetCreditDialog(false);
          }}
        />
      )}
      <Box
        sx={{
          marginBottom: '24px',
          marginLeft: '12px',
          marginTop: '12px',
          display: 'flex',
          ...layoutOverrides?.inShowRoomStyleOverrides,
        }}
      >
        {inShowroomLocation === 'top' && inShowroomToggleComponent}
        {inShowroomLocation === 'top' && addCoapplicantToggleComponent}
        {showCreditChip && (
          <Chip
            sx={{ marginLeft: 'auto' }}
            disabled={disabled || (!!editKey && formMode == 'edit')}
            label={`Credit range: ${creditRange}`}
          />
        )}
      </Box>
      <FormSectionLayout
        formMode={formMode}
        sections={getSections(editKey)}
        editKey={editKey}
        onEdit={(newEditKey) => {
          if (!!deal?.submittedToLendersAt && newEditKey) {
            setShowResetCreditDialog(true);
          }

          setEditKey(newEditKey as CustomerFormSections);
          if (formMode === 'add') {
            return;
          }

          if (
            newEditKey &&
            newEditKey === CustomerFormSections.coapplicantAddress
          ) {
            setShouldCoApplicantAddressEditRemainOpen(true);
          }
          if (newEditKey && newEditKey === CustomerFormSections.address) {
            setShouldAddressEditRemainOpen(true);
          }

          if (newEditKey === CustomerFormSections.contact) {
            track?.(customerFormEventsName.EDIT_CONTACT_INFO_START);
          }

          if (newEditKey === CustomerFormSections.lead) {
            track?.(customerFormEventsName.EDIT_LEAD_INFO_START);
          }

          if (newEditKey === CustomerFormSections.address) {
            track?.(customerFormEventsName.EDIT_ADDRESS_START);
          }
        }}
        onSave={() => onEditSaveHandler(editKey as CustomerFormSections)}
        isSaving={isSaving}
        elevation={layoutOverrides?.paper?.elevation}
        addText={formTexts.addAddressText}
        backgroundColor={layoutOverrides?.paper?.backgroundColor}
        disabled={disabled}
      />
      <Box
        sx={{
          marginBottom: '24px',
          display: 'flex',
          ...layoutOverrides?.inShowRoomStyleOverrides,
        }}
      >
        {inShowroomLocation === 'bottom' && inShowroomToggleComponent}
        {inShowroomLocation === 'bottom' && addCoapplicantToggleComponent}
      </Box>
      {showCreditAppLink && (
        <StyledLink
          component={'a'}
          styleOverrides={{
            margin: '64px 0 0',
            display: 'block',
            color: 'var(--commander-colorPrimary)',
          }}
          href={`${creditAppUrl}?dealId=${deal?.id}`}
          target="_blank"
        >
          Credit app
        </StyledLink>
      )}
    </Paper>
  );
};

export default CustomerForm;
